class ThemeDialog extends HTMLElement {
  static instanceCount = 0
  static closeDurationMs = 300
  static pushDrawerStorageKey = 'theme:open-push-drawer'
  static persistentPushDrawerId = 'cart-dialog'

  connectedCallback () {
    if (this.isInitialized) return

    this.dialog = this.querySelector('dialog')

    if (!this.dialog) return

    this.openedByPointer = false

    if (!this.dialog.id) {
      ThemeDialog.instanceCount += 1
      this.dialog.id = `theme-dialog-${ThemeDialog.instanceCount}`
    }

    this.listenerController = new AbortController()
    this.triggers = Array.from(document.querySelectorAll(`[data-toggle="${this.dialog.id}"]`))
    this.pageContainer = document.querySelector('#page-container')
    this.onDialogClose = this.onDialogClose.bind(this)
    this.onDialogCancel = this.onDialogCancel.bind(this)
    this.onDialogClick = this.onDialogClick.bind(this)
    this.onInternalClick = this.onInternalClick.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)

    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-controls', this.dialog.id)
      trigger.setAttribute('aria-expanded', 'false')
      trigger.setAttribute('aria-haspopup', 'dialog')
      trigger.addEventListener('click', this.onTriggerClick.bind(this, trigger), {
        signal: this.listenerController.signal
      })
    })

    this.dialog.addEventListener('close', this.onDialogClose, { signal: this.listenerController.signal })
    this.dialog.addEventListener('cancel', this.onDialogCancel, { signal: this.listenerController.signal })
    this.dialog.addEventListener('click', this.onDialogClick, { signal: this.listenerController.signal })
    this.addEventListener('click', this.onInternalClick, { signal: this.listenerController.signal })
    document.addEventListener('click', this.onDocumentClick, { signal: this.listenerController.signal })
    document.addEventListener('keydown', this.onDocumentKeydown, { signal: this.listenerController.signal })
    this.isInitialized = true
    this.restorePushDrawerState()
    this.setupHashNavigation()
  }

  disconnectedCallback () {
    this.unlockPageScroll()
    this.updatePushDrawerState(this.dialog)
    window.removeEventListener('hashchange', this.onHashChange)
    this.listenerController?.abort()
    window.clearTimeout(this.closeTimer)
    this.isInitialized = false
  }

  onTriggerClick (trigger, event) {
    event.preventDefault()
    this.lastTrigger = trigger
    this.openedByPointer = event.detail !== 0
    this.openDialog()
  }

  openDialog () {
    if (this.dialog.open) {
      this.requestClose()
      return
    }

    this.closeOtherOpenDialogs()
    this.dialog.classList.remove('is-closing')
    this.isClosing = false
    this.isPushMode = this.dialog.classList.contains('drawer-right') && window.matchMedia('(min-width: 1401px)').matches
    this.dialog.dataset.openedBy = this.openedByPointer ? 'pointer' : 'keyboard'

    if (this.isPushMode) {
      this.dialog.show()
      document.body.classList.add('push-drawer-open')
      this.savePushDrawerState()
    } else {
      this.dialog.showModal()
    }

    this.setExpandedState(true)
    if (!this.isPushMode) this.lockPageScroll()
  }

  onDialogClose () {
    this.dialog.classList.remove('is-closing')
    delete this.dialog.dataset.openedBy
    delete this.dialog.dataset.restored
    this.setExpandedState(false)
    this.isClosing = false
    if (!document.querySelector('dialog:modal')) this.unlockPageScroll()
    if (this.isPushMode) this.clearPushDrawerState()
    this.isPushMode = false
    this.updatePushDrawerState()
    window.clearTimeout(this.closeTimer)
    this.closeTimer = null
    this.lastTrigger?.focus()
    this.removeCartHash()
  }

  onDialogCancel (event) {
    event.preventDefault()
    this.requestClose()
  }

  onDialogClick (event) {
    if (event.target !== this.dialog) return

    const rect = this.dialog.getBoundingClientRect()
    const clickedOutside = event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom

    if (clickedOutside) this.requestClose()
  }

  onInternalClick (event) {
    const closeButton = event.target.closest('[data-dialog-close]')

    if (closeButton && this.contains(closeButton)) this.requestClose()
  }

  onDocumentClick (event) {
    if (!this.dialog.open || !this.isPushMode) return
    if (this.pageContainer?.contains(event.target) || this.dialog.contains(event.target)) return

    this.requestClose()
  }

  onDocumentKeydown (event) {
    if (event.key === 'Tab') delete this.dialog.dataset.openedBy
    if (event.key === 'Escape' && this.dialog.open && this.isPushMode) this.requestClose()
  }

  updatePushDrawerState (excludedDialog) {
    const hasOpenPushDrawer = Array.from(document.querySelectorAll('dialog.drawer-right[open]'))
      .some((dialog) => dialog !== excludedDialog && !dialog.matches(':modal'))

    document.body.classList.toggle('push-drawer-open', hasOpenPushDrawer)
  }

  restorePushDrawerState () {
    if (this.dialog.id !== ThemeDialog.persistentPushDrawerId) return
    if (!window.matchMedia('(min-width: 1401px)').matches) return

    try {
      if (window.localStorage.getItem(ThemeDialog.pushDrawerStorageKey) === this.dialog.id) {
        if (this.dialog.open) {
          this.isPushMode = true
          document.body.classList.add('push-drawer-open')
          this.setExpandedState(true)
        } else {
          this.openDialog()
        }
      }
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }

  setupHashNavigation () {
    if (this.dialog.id !== ThemeDialog.persistentPushDrawerId) return

    this.onHashChange = this.openCartFromHash.bind(this)
    window.addEventListener('hashchange', this.onHashChange)
    this.openCartFromHash()
  }

  openCartFromHash () {
    if (window.location.hash.toLowerCase() !== '#cart' || this.dialog.open) return

    const lazyStylesheet = document.getElementById('lazy-styles')
    const openDialog = () => {
      if (!this.isConnected || this.dialog.open) return

      this.lastTrigger = document.querySelector(`[data-toggle="${this.dialog.id}"]`)
      this.openDialog()
    }

    if (lazyStylesheet && !lazyStylesheet.sheet) {
      lazyStylesheet.setAttribute('blocking', 'render')
      lazyStylesheet.addEventListener('load', openDialog, { once: true })
      lazyStylesheet.addEventListener('error', openDialog, { once: true })
      lazyStylesheet.media = 'all'
      return
    }

    openDialog()
  }

  removeCartHash () {
    if (this.dialog.id !== ThemeDialog.persistentPushDrawerId) return
    if (window.location.hash.toLowerCase() !== '#cart') return

    const url = new URL(window.location.href)

    url.hash = ''
    window.history.replaceState({}, '', `${url.pathname}${url.search}`)
  }

  savePushDrawerState () {
    if (this.dialog.id !== ThemeDialog.persistentPushDrawerId) return

    try {
      window.localStorage.setItem(ThemeDialog.pushDrawerStorageKey, this.dialog.id)
    } catch {
      // The drawer remains usable when storage is unavailable.
    }
  }

  clearPushDrawerState () {
    if (this.dialog.id !== ThemeDialog.persistentPushDrawerId) return

    try {
      if (window.localStorage.getItem(ThemeDialog.pushDrawerStorageKey) === this.dialog.id) {
        window.localStorage.removeItem(ThemeDialog.pushDrawerStorageKey)
        delete document.documentElement.dataset.pushDrawerOpen
      }
    } catch {
      // The drawer remains usable when storage is unavailable.
    }
  }

  requestClose () {
    if (!this.dialog?.open || this.isClosing) return

    this.isClosing = true
    this.dialog.classList.add('is-closing')
    if (this.isPushMode) this.clearPushDrawerState()

    const closeDurationMs = this.getCloseDurationMs()

    if (closeDurationMs === 0) {
      this.dialog.close()
      return
    }

    window.clearTimeout(this.closeTimer)
    this.closeTimer = window.setTimeout(() => this.dialog.close(), closeDurationMs)
  }

  getCloseDurationMs () {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const designMode = document.documentElement.dataset.designMode === 'true'

    if (reduceMotion || designMode) return 0

    const duration = window.getComputedStyle(this.dialog).getPropertyValue('--dialog-transition-duration').trim()
    const parsedDuration = Number.parseFloat(duration)

    if (!Number.isFinite(parsedDuration) || parsedDuration < 0) return ThemeDialog.closeDurationMs

    return duration.endsWith('ms') ? parsedDuration : parsedDuration * 1000
  }

  setExpandedState (isExpanded) {
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', String(isExpanded)))
  }

  closeOtherOpenDialogs () {
    document.querySelectorAll('dialog[open]').forEach((openDialog) => {
      if (openDialog === this.dialog) return

      const dialogHost = openDialog.closest('theme-dialog')

      if (dialogHost?.requestClose) {
        dialogHost.requestClose()
      } else {
        openDialog.close()
      }
    })
  }

  lockPageScroll () {
    if (document.body.dataset.scrollLocked === 'true') return

    const scrollY = window.scrollY

    document.body.dataset.scrollLocked = 'true'
    document.body.dataset.scrollLockTop = String(scrollY)
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'
  }

  unlockPageScroll () {
    if (document.body.dataset.scrollLocked !== 'true') return

    const scrollY = Number(document.body.dataset.scrollLockTop || 0)

    delete document.body.dataset.scrollLocked
    delete document.body.dataset.scrollLockTop
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    document.body.style.width = ''
    window.scrollTo(0, scrollY)
  }
}

if (!customElements.get('theme-dialog')) {
  customElements.define('theme-dialog', ThemeDialog)
}

class ThemeCollapse extends HTMLElement {
  connectedCallback () {
    if (this.isInitialized) return

    this.details = this.querySelector('.theme-collapse-details')
    this.summary = this.querySelector('.theme-collapse-summary')

    if (!this.details || !this.summary) return

    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.onSummaryClick = this.handleSummaryClick.bind(this)
    this.summary.addEventListener('click', this.onSummaryClick)
    this.isInitialized = true
  }

  disconnectedCallback () {
    this.summary?.removeEventListener('click', this.onSummaryClick)
    this.finishAnimation?.()
    this.isInitialized = false
  }

  handleSummaryClick (event) {
    event.preventDefault()

    if (!this.details || !this.summary || this.details.dataset.animating === 'true') return

    const isOpen = this.details.open

    if (this.reduceMotionQuery.matches) {
      this.details.open = !isOpen
      return
    }

    const startHeight = this.details.offsetHeight

    if (!isOpen) this.details.open = true

    const endHeight = isOpen ? this.summary.offsetHeight : this.details.scrollHeight

    this.details.dataset.animating = 'true'
    this.details.style.height = `${startHeight}px`
    this.details.style.overflow = 'hidden'

    window.requestAnimationFrame(() => {
      this.details.style.height = `${endHeight}px`
    })

    const cleanup = () => {
      if (isOpen) this.details.open = false

      this.details.style.height = ''
      this.details.style.overflow = ''
      this.details.dataset.animating = 'false'
      this.details.removeEventListener('transitionend', onTransitionEnd)
      window.clearTimeout(this.animationTimer)
      this.finishAnimation = null
    }

    const onTransitionEnd = (transitionEvent) => {
      if (transitionEvent.target !== this.details || transitionEvent.propertyName !== 'height') return

      cleanup()
    }

    this.finishAnimation = cleanup
    this.details.addEventListener('transitionend', onTransitionEnd)
    this.animationTimer = window.setTimeout(cleanup, 350)
  }
}

if (!customElements.get('theme-collapse')) {
  customElements.define('theme-collapse', ThemeCollapse)
}

class CarouselSlider extends HTMLElement {
  connectedCallback () {
    if (this.isInitialized) return

    this.designMode = document.documentElement.dataset.designMode === 'true'
    this.track = this.querySelector('[data-carousel-track]')
    this.items = Array.from(this.querySelectorAll('[data-carousel-item]'))
    this.previousButton = this.querySelector('[data-carousel-previous]')
    this.nextButton = this.querySelector('[data-carousel-next]')
    this.progressBar = this.querySelector('[data-carousel-progress-bar]')
    this.controlAlignmentElement = this.items[0]?.querySelector('[data-carousel-control-alignment]')
    this.trackWrapper = this.querySelector('.carousel-track-wrapper')

    if (!this.track || !this.items.length) return

    this.onClick = this.onClick.bind(this)
    this.onScroll = this.onScroll.bind(this)
    this.onResize = this.onResize.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onAlignmentLoad = this.scheduleLayoutUpdate.bind(this)

    this.addEventListener('click', this.onClick)
    this.track.addEventListener('scroll', this.onScroll, { passive: true })
    this.track.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.onResize)

    if (this.controlAlignmentElement instanceof HTMLImageElement && !this.controlAlignmentElement.complete) {
      this.controlAlignmentElement.addEventListener('load', this.onAlignmentLoad, { once: true })
    }

    if ('ResizeObserver' in window && this.controlAlignmentElement) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayoutUpdate())
      this.resizeObserver.observe(this.controlAlignmentElement)
    }

    this.isInitialized = true
    this.update()
  }

  disconnectedCallback () {
    if (!this.isInitialized) return

    this.removeEventListener('click', this.onClick)
    this.track.removeEventListener('scroll', this.onScroll)
    this.track.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.onResize)
    this.controlAlignmentElement?.removeEventListener('load', this.onAlignmentLoad)
    this.resizeObserver?.disconnect()
    window.cancelAnimationFrame(this.scrollFrame)
    window.cancelAnimationFrame(this.layoutFrame)
    this.isInitialized = false
  }

  onClick (event) {
    if (event.target.closest('[data-carousel-previous]')) {
      this.goPrevious()
    }

    if (event.target.closest('[data-carousel-next]')) {
      this.goNext()
    }

  }

  onScroll () {
    window.cancelAnimationFrame(this.scrollFrame)
    this.scrollFrame = window.requestAnimationFrame(() => this.update())
  }

  onResize () {
    this.scheduleLayoutUpdate()
  }

  onKeydown (event) {
    if (event.target !== this.track) return
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

    event.preventDefault()

    if (event.key === 'ArrowRight') {
      this.goNext()
    } else {
      this.goPrevious()
    }
  }

  getCurrentIndex () {
    return this.getIndexAtScroll(this.track.scrollLeft)
  }

  getIndexAtScroll (scrollPosition) {
    let closestIndex = 0
    let closestDistance = Infinity

    this.items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - this.track.offsetLeft - scrollPosition)

      if (distance < closestDistance) {
        closestIndex = index
        closestDistance = distance
      }
    })

    return closestIndex
  }

  goPrevious () {
    if (this.track.scrollLeft <= 1) {
      this.scrollToPosition(this.track.scrollWidth - this.track.clientWidth)
      return
    }

    this.goTo(this.getCurrentIndex() - 1)
  }

  goNext () {
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth)

    if (this.track.scrollLeft >= maxScroll - 1) {
      this.scrollToPosition(0)
      return
    }

    this.goTo(this.getCurrentIndex() + 1)
  }

  goTo (index, smooth = true) {
    const nextIndex = Math.max(0, Math.min(index, this.items.length - 1))
    const item = this.items[nextIndex]

    if (!item) return

    this.scrollToPosition(item.offsetLeft - this.track.offsetLeft, smooth)
  }

  scrollToPosition (left, smooth = true) {
    this.track.scrollTo({
      left,
      behavior: smooth && !this.designMode && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto'
    })
  }

  positionControls () {
    if (!this.controlAlignmentElement || !this.previousButton || !this.nextButton) return
    if (!this.trackWrapper) return

    let alignmentTop = 0
    let offsetElement = this.controlAlignmentElement

    while (offsetElement && offsetElement !== this.trackWrapper) {
      alignmentTop += offsetElement.offsetTop
      offsetElement = offsetElement.offsetParent
    }

    if (offsetElement !== this.trackWrapper) return

    const controlTop = alignmentTop + (this.controlAlignmentElement.offsetHeight / 2)

    if (controlTop <= 0) return

    this.previousButton.style.top = `${controlTop}px`
    this.nextButton.style.top = `${controlTop}px`
  }

  scheduleLayoutUpdate () {
    if (this.layoutFrame) return

    this.layoutFrame = window.requestAnimationFrame(() => {
      this.layoutFrame = null
      this.update()
    })
  }

  updateItemAccessibility () {
    const firstItem = this.items[0]

    if (!firstItem) return

    const trackStyles = window.getComputedStyle(this.track)
    const gap = Number.parseFloat(trackStyles.columnGap) || 0
    const itemWidth = firstItem.getBoundingClientRect().width

    if (itemWidth <= 0 || this.track.clientWidth <= 0) return

    const visibleItemCount = Math.max(1, Math.round((this.track.clientWidth + gap) / (itemWidth + gap)))
    const maxStartIndex = Math.max(0, this.items.length - visibleItemCount)
    const startIndex = Math.min(this.getCurrentIndex(), maxStartIndex)
    const endIndex = startIndex + visibleItemCount

    this.items.forEach((item, index) => {
      const isVisible = index >= startIndex && index < endIndex

      if (!isVisible && item.contains(document.activeElement)) {
        this.track.focus({ preventScroll: true })
      }

      item.toggleAttribute('inert', !isVisible)

      if (isVisible) {
        item.removeAttribute('aria-hidden')
      } else {
        item.setAttribute('aria-hidden', 'true')
      }
    })
  }

  update () {
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth)
    const isScrollable = maxScroll > 1

    this.dataset.scrollable = String(isScrollable)
    this.positionControls()
    this.updateItemAccessibility()

    if (this.progressBar && this.track.scrollWidth > 0) {
      const scrollLeft = Math.min(Math.max(this.track.scrollLeft, 0), maxScroll)
      const progressWidth = (this.track.clientWidth / this.track.scrollWidth) * 100
      const progressTrackWidth = this.progressBar.parentElement?.clientWidth || 0
      const progressOffset = progressTrackWidth * (scrollLeft / this.track.scrollWidth)

      this.progressBar.style.width = `${progressWidth}%`
      this.progressBar.style.transform = `translateX(${progressOffset}px)`
    }

  }
}

if (!customElements.get('carousel-slider')) {
  customElements.define('carousel-slider', CarouselSlider)
}
